import React, { useMemo, useState } from 'react';
import { useCSVStore } from '../../stores/csvStore';
import './ValidationResults.css';
import AIAnalysisButton from './AIAnalysisButton';

interface ValidationResultsProps {
  onConfirm: () => void;
  onReupload: () => void;
  hideAnalysisButton?: boolean;
}

interface SortableData {
  row: string[];
  year: number | null;
  month: number | null;
  originalIndex: number;
}

const ValidationResults: React.FC<ValidationResultsProps> = ({ 
  onConfirm, 
  onReupload,
  hideAnalysisButton = false
}) => {
  const { validationResults, errors, warnings, csvData, uploadedFile } = useCSVStore();
  const [userAccepted, setUserAccepted] = useState(false);

  // Prepare sorted data based on year and month
  const sortedData = useMemo(() => {
    if (!csvData || csvData.length <= 1) return [];
    
    const headers = csvData[0].map(h => (h || '').toString().toLowerCase());
    const rows = csvData.slice(1);
    
    // Find year and month columns
    const yearIndex = headers.findIndex(h => h.includes('year'));
    const monthIndex = headers.findIndex(h => h.includes('month'));
    const dateIndex = headers.findIndex(h => h.includes('date'));
    
    const sortableData: SortableData[] = rows.map((row, index) => {
      let year: number | null = null;
      let month: number | null = null;
      
      // Try to extract year and month from various column types
      if (yearIndex !== -1) {
        const yearValue = row[yearIndex];
        if (yearValue) year = parseInt(yearValue.toString(), 10);
      }
      
      if (monthIndex !== -1) {
        const monthValue = row[monthIndex];
        if (monthValue) {
          // Try parsing as number first
          const monthNum = parseInt(monthValue.toString(), 10);
          if (!isNaN(monthNum) && monthNum >= 1 && monthNum <= 12) {
            month = monthNum;
          } else {
            // Try parsing month names
            const monthNames = [
              'january', 'february', 'march', 'april', 'may', 'june',
              'july', 'august', 'september', 'october', 'november', 'december'
            ];
            const monthIdx = monthNames.findIndex(m => 
              monthValue.toString().toLowerCase().includes(m)
            );
            if (monthIdx !== -1) month = monthIdx + 1;
          }
        }
      }
      
      // If we have a date column but no year/month, try to extract from date
      if ((year === null || month === null) && dateIndex !== -1) {
        const dateValue = row[dateIndex];
        if (dateValue) {
          try {
            const date = new Date(dateValue.toString());
            if (!isNaN(date.getTime())) {
              if (year === null) year = date.getFullYear();
              if (month === null) month = date.getMonth() + 1;
            }
          } catch (e) {
            // Failed to parse date, ignore
          }
        }
      }
      
      return {
        row,
        year,
        month,
        originalIndex: index
      };
    });
    
    // Sort by year (desc) and month (desc)
    return sortableData.sort((a, b) => {
      // If both have year, sort by year
      if (a.year !== null && b.year !== null) {
        if (a.year !== b.year) return b.year - a.year; // desc
        
        // If same year and both have month, sort by month
        if (a.month !== null && b.month !== null) {
          return b.month - a.month; // desc
        }
        
        // If only one has month
        if (a.month !== null) return -1;
        if (b.month !== null) return 1;
      }
      
      // If only one has year
      if (a.year !== null) return -1;
      if (b.year !== null) return 1;
      
      // No sorting criteria, keep original order
      return a.originalIndex - b.originalIndex;
    });
  }, [csvData]);

  if (!validationResults) {
    return (
      <div className="validation-placeholder">
        <p>No validation results available yet.</p>
      </div>
    );
  }

  const isValid = validationResults.valid;
  const hasWarningsOnly = warnings.length > 0 && errors.length === 0;
  
  const handleConfirm = () => {
    if (userAccepted) {
      onConfirm();
    }
  };

  return (
    <div className={`validation-results ${isValid ? 'valid' : 'invalid'}`}>
      {isValid ? (
        <div className="validation-success">
          <div className="success-icon">✓</div>
          <h3>Validation Successful</h3>
          <p>Your file has passed all validation checks and is ready to be processed.</p>
          
          <div className="user-acceptance">
            <label className="acceptance-checkbox">
              <input 
                type="checkbox" 
                checked={userAccepted}
                onChange={(e) => setUserAccepted(e.target.checked)}
              />
              <span>
                I have reviewed the data and confirm it is correct for processing
              </span>
            </label>
          </div>
          
          <div className="validation-actions">
            <button 
              className={`primary-button ${!userAccepted ? 'disabled' : ''}`} 
              onClick={handleConfirm}
              disabled={!userAccepted}
            >
              Continue to Next Step
            </button>
          </div>
        </div>
      ) : (
        <div className="validation-errors">
          <h3>Validation Issues Found</h3>
          <p>Please address the following issues before proceeding:</p>
          
          <div className="issues-container">
            {errors.length > 0 && (
              <div className="error-section">
                <h4>{errors.length} Error{errors.length !== 1 ? 's' : ''}</h4>
                <ul className="error-list">
                  {errors.map((error, index) => (
                    <li key={index} className="error-item">
                      <div className="error-icon">⚠️</div>
                      <div className="error-content">
                        <div className="error-location">
                          {error.row === 0 ? 'Headers' : `Row ${error.row}`}
                          {parseInt(error.column) !== 0 && `, Column ${error.column}`}
                        </div>
                        <div className="error-message">{error.message}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {warnings.length > 0 && (
              <div className="warning-section">
                <h4>{warnings.length} Warning{warnings.length !== 1 ? 's' : ''}</h4>
                <ul className="warning-list">
                  {warnings.map((warning, index) => (
                    <li key={index} className="warning-item">
                      <div className="warning-icon">⚠️</div>
                      <div className="warning-content">
                        <div className="warning-location">
                          {warning.row === 0 ? 'Headers' : `Row ${warning.row}`}
                          {parseInt(warning.column) !== 0 && `, Column ${warning.column}`}
                        </div>
                        <div className="warning-message">{warning.message}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          
          {hasWarningsOnly && (
            <div className="user-acceptance">
              <label className="acceptance-checkbox">
                <input 
                  type="checkbox" 
                  checked={userAccepted}
                  onChange={(e) => setUserAccepted(e.target.checked)}
                />
                <span>
                  I acknowledge the warnings and confirm the data is suitable for processing
                </span>
              </label>
            </div>
          )}
          
          <div className="validation-actions">
            <button className="secondary-button" onClick={onReupload}>
              Upload Another File
            </button>
            {hasWarningsOnly && (
              <button 
                className={`primary-button ${!userAccepted ? 'disabled' : ''}`}
                onClick={handleConfirm}
                disabled={!userAccepted}
              >
                Continue with Warnings
              </button>
            )}
          </div>
        </div>
      )}
      
      // Data Preview Section - Show regardless of validation status
      // REMOVED: Data preview section per user request
    </div>
  );
};

// Helper function to check if a value is numeric
const isNumeric = (value: string): boolean => {
  if (!value) return false;
  return !isNaN(Number(value.replace(/[$,]/g, '')));
};

export default ValidationResults; 